<template>
  <teleport to="body">
    <transition
      enter-active-class="transition ease-out duration-200 transform"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-200 transform"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        ref="modal-backdrop"
        v-show="show"
        class="picture-modal-backdrop fixed z-10 inset-0 overflow-y-auto bg-backdrop"
      >
        <div
          class="picture-modal-positioner flex items-start justify-center min-h-screen pt-24 text-center"
        >
          <div
            class="picture-modal-dialog relative card-global rounded-xl text-left overflow-hidden w-1/2"
            role="dialog"
            ref="modal"
            aria-modal="true"
            aria-labelledby="modal-headline"
          >
            <div class="pb-5">
              <button
                type="button"
                class="picture-modal-close absolute top-4 right-4"
                @click="closeModal"
              >
                {{ $t("common.close") }}
              </button>
            </div>

            <div class="modal-content">
              <div class="picture-modal-editor flex">
                <cropper
                  class="cropper"
                  ref="cropper"
                  :src="image.src"
                  :debounce="false"
                  :stencil-props="{
                    aspectRatio: 1,
                  }"
                  @change="onChange"
                />
                <preview
                  class="rounded-full ml-3"
                  :width="200"
                  :height="200"
                  :image="result.image"
                  :coordinates="result.coordinates"
                />
              </div>

              <div class="button-wrapper mt-5 flex">
                <Button @click="$refs.file.click()">
                  <input
                    class="cropper__file-input"
                    type="file"
                    ref="file"
                    @change="loadImage($event)"
                    accept="image/*"
                  />
                  Загрузить
                </Button>
                <Button
                  v-if="result.image"
                  v-on:click="handleUpload"
                  class="flex ml-5"
                >
                  Сохранить
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { defineComponent } from "vue";
import { Cropper, Preview } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import Button from "@/components/common/Button";
import { UserActions } from "@/store/modules/user/actions";
import { UserGetters } from "@/store/modules/user/getters";
import { AuthActions, AuthGetters } from "@/store/modules/auth";

// This function is used to detect the actual image type,
function getMimeType(file, fallback = null) {
  const byteArray = new Uint8Array(file).subarray(0, 4);
  let header = "";
  for (let i = 0; i < byteArray.length; i++) {
    header += byteArray[i].toString(16);
  }
  switch (header) {
    case "89504e47":
      return "image/png";
    case "47494638":
      return "image/gif";
    case "ffd8ffe0":
    case "ffd8ffe1":
    case "ffd8ffe2":
    case "ffd8ffe3":
    case "ffd8ffe8":
      return "image/jpeg";
    default:
      return fallback;
  }
}

export default defineComponent({
  components: {
    Button,
    Cropper,
    Preview,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["save", "close"],
  data() {
    return {
      image: {
        src: null,
        type: null,
      },
      filename: null,
      fileType: null,
      result: {
        coordinates: null,
        image: null,
      },
    };
  },
  methods: {
    onChange({ coordinates, image }) {
      this.result = {
        coordinates,
        image,
      };
    },
    reset() {
      this.image = {
        src: null,
        type: null,
      };
    },
    loadImage(event) {
      // Reference to the DOM input element
      const { files } = event.target;
      // Ensure that you have a file before attempting to read it
      if (files && files[0]) {
        // 1. Revoke the object URL, to allow the garbage collector to destroy the uploaded before file
        if (this.image.src) {
          URL.revokeObjectURL(this.image.src);
        }
        // 2. Create the blob link to the file to optimize performance:
        const blob = URL.createObjectURL(files[0]);
        this.fileName = files[0].name;
        this.fileType = files[0].type;
        // 3. The steps below are designated to determine a file mime type to use it during the
        // getting of a cropped image from the canvas. You can replace it them by the following string,
        // but the type will be derived from the extension and it can lead to an incorrect result:
        //
        // this.image = {
        //    src: blob;
        //    type: files[0].type
        // }

        // Create a new FileReader to read this image binary data
        const reader = new FileReader();
        // Define a callback function to run, when FileReader finishes its job
        reader.onload = (e) => {
          // Note: arrow function used here, so that "this.image" refers to the image of Vue component
          this.image = {
            // Set the image source (it will look like blob:http://example.com/2c5270a5-18b5-406e-a4fb-07427f5e7b94)
            src: blob,
            // Determine the image type to preserve it during the extracting the image from canvas:
            type: getMimeType(e.target.result, files[0].type),
          };
        };
        // Start the reader job - read file as a data url (base64 format)
        reader.readAsArrayBuffer(files[0]);
      }
    },
    closeModal() {
      this.$emit("close");
    },
    handleUpload() {
      const { canvas } = this.$refs.cropper.getResult();
      if (canvas) {
        const form = new FormData();
        canvas.toBlob((blob) => {
          form.append("imageGuid", "");
          form.append("image", blob, this.fileName);
          this.$store
            .dispatch(UserActions.PROFILE_IMAGE_UPLOAD, form)
            .then(async (response) => {
              await this.$store.dispatch(UserActions.PROFILE_IMAGE_UPDATE, {
                imageGuid: response.data.imageGuid,
              });

              this.$emit("save");
            });
        }, this.fileType);
      }
    },
  },
  unmounted() {
    // Revoke the object URL, to allow the garbage collector to destroy the uploaded before file
    if (this.image.src) {
      URL.revokeObjectURL(this.image.src);
    }
  },
});
</script>

<style scoped>
.cropper {
  width: 75%;
  background: #ddd;
}

.cropper__file-input {
  display: none;
}

.picture-modal-dialog {
  width: min(50%, 800px);
  max-height: calc(100vh - 7rem);
  max-height: calc(100dvh - 7rem);
  overflow-y: auto;
}

.picture-modal-close {
  min-width: 40px;
  min-height: 40px;
}

@media (max-width: 768px) {
  .picture-modal-backdrop {
    padding: max(12px, env(safe-area-inset-top))
      max(12px, env(safe-area-inset-right))
      max(12px, env(safe-area-inset-bottom))
      max(12px, env(safe-area-inset-left));
  }

  .picture-modal-positioner {
    min-height: 100%;
    align-items: center;
    padding-top: 0;
  }

  .picture-modal-dialog {
    width: 100%;
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
  }

  .picture-modal-editor {
    align-items: center;
    flex-direction: column;
    gap: 16px;
  }

  .cropper {
    width: 100%;
    min-height: 280px;
  }

  .picture-modal-editor :deep(.vue-advanced-cropper__preview) {
    margin-left: 0;
  }

  .button-wrapper {
    flex-wrap: wrap;
    gap: 12px;
  }

  .button-wrapper > * {
    margin-left: 0 !important;
  }
}
</style>
